import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Global spring physics for snappy, premium Apple-like animations
const defaultSpring = { type: 'spring', stiffness: 100, damping: 20 };

// Staggered text container variants
const textContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Individual text line variants
const textItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: defaultSpring 
  },
};

const LandingPage = () => {
  // State for the interactive MacBook dashboard demo
  const [overrideMode, setOverrideMode] = useState('closed');

  // Surgical fix for iOS/mobile video autoplay restrictions
  useEffect(() => {
    const optimizeMobileVideos = () => {
      const videos = document.querySelectorAll('video');
      videos.forEach(video => {
        // Force attributes required by strict mobile browsers
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        
        // Attempt to play explicitly to bypass passive blocking
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            // If power saving mode or strict policies block it, ensure it's muted and try again
            video.muted = true;
            video.play().catch(() => {
              // Silently handle final rejection to prevent console spam
            });
          });
        }
      });
    };

    // Run on mount
    optimizeMobileVideos();
    
    // Also run when window regains focus, as iOS sometimes pauses videos in background
    window.addEventListener('focus', optimizeMobileVideos);
    // And on touch start to guarantee playback if initial autoplay was blocked
    window.addEventListener('touchstart', optimizeMobileVideos, { once: true });

    return () => {
      window.removeEventListener('focus', optimizeMobileVideos);
      window.removeEventListener('touchstart', optimizeMobileVideos);
    };
  }, []);

  // Trigger function for the Webability Accessibility Widget
  const toggleAccessibility = (e) => {
    e.preventDefault();
    // The widget injects a button into the DOM. By simulating a click on it, 
    // we can open the menu even when the default floating icon is hidden.
    const widgetBtn = document.querySelector('.asw-menu-btn') || document.querySelector('[data-asw-icon-type]');
    if (widgetBtn) {
      widgetBtn.click();
    }
  };

  // Configuration for the dynamic status monitor
  const statusConfig = {
    closed: { heading: 'STUDIO CLOSED', sub: 'Studio locked manually', containerClass: 'border-zinc-700/50 bg-zinc-800/10', textClass: 'text-zinc-200' },
    open: { heading: 'STUDIO OPEN', sub: 'Studio hold open', containerClass: 'border-emerald-500/30 bg-emerald-500/5', textClass: 'text-emerald-400' },
    leadin: { heading: 'STANDBY', sub: 'Next recording starts shortly', containerClass: 'border-amber-500/30 bg-amber-500/5', textClass: 'text-amber-400' },
    recording: { heading: 'RECORDING', sub: 'Recording mode forced', containerClass: 'border-red-500/30 bg-red-500/5', textClass: 'text-red-500' },
    wrapup: { heading: 'WRAPPING UP', sub: 'Session concluding shortly', containerClass: 'border-blue-500/30 bg-blue-500/5', textClass: 'text-blue-400' },
    break: { heading: 'ON BREAK', sub: 'Staff away from deck', containerClass: 'border-purple-500/30 bg-purple-500/5', textClass: 'text-purple-400' },
  };

  // Configuration for the 6-button grid
  const dashboardButtons = [
    { id: 'open', label: 'OPEN', activeClass: 'border-emerald-500 text-emerald-400 bg-emerald-500/10' },
    { id: 'recording', label: 'RECORDING', activeClass: 'border-red-500 text-red-500 bg-red-500/10' },
    { id: 'leadin', label: 'LEAD-IN', activeClass: 'border-amber-500 text-amber-400 bg-amber-500/10' },
    { id: 'wrapup', label: 'WRAP UP', activeClass: 'border-blue-500 text-blue-400 bg-blue-500/10' },
    { id: 'break', label: 'BREAK', activeClass: 'border-purple-500 text-purple-400 bg-purple-500/10' },
    { id: 'closed', label: 'CLOSED', activeClass: 'border-zinc-500 text-zinc-200 bg-zinc-500/10' }
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex flex-col antialiased selection:bg-zinc-800 selection:text-white overflow-x-hidden">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/70 backdrop-blur-md border-b border-white/5">
        <div className="max-w-screen-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-xl tracking-tight">
              <span className="font-light">Studio</span>
              <span className="font-bold">Sign</span>
            </span>
          </div>
          
          <div className="flex items-center gap-6">
            <a 
              href="https://web.studiosign.app/login" 
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors duration-200"
            >
              Log In
            </a>
            <a 
              href="https://web.studiosign.app/signup" 
              className="text-sm font-medium bg-white text-black px-5 py-2.5 rounded-full hover:bg-zinc-200 hover:scale-105 transition-all duration-300"
            >
              Create Account
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="flex-1 flex flex-col items-center justify-start pt-32 lg:pt-40 pb-20 px-6 text-center">
        <motion.div
          variants={textContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="flex flex-col items-center"
        >
          <motion.h1 variants={textItemVariants} className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.1]">
            Put your studio doors on autopilot.
          </motion.h1>
          
          <motion.p variants={textItemVariants} className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
            Protect your recording rooms from interruptions. Sync your door status displays natively from your calendar so no one knocks during a hot take.
          </motion.p>
          
          <motion.div variants={textItemVariants} className="mt-8">
            <a 
              href="https://web.studiosign.app/signup"
              className="inline-flex items-center justify-center bg-white text-black px-8 py-3.5 text-lg font-medium rounded-full hover:bg-zinc-200 hover:scale-105 transition-all duration-300"
            >
              Create Account
            </a>
          </motion.div>
        </motion.div>

        {/* Media Container - iPad Mockup */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ ...defaultSpring, delay: 0.2 }}
          className="mt-16 w-full max-w-5xl mx-auto relative z-10 px-4 sm:px-6"
        >
          {/* Ambient Glow Behind iPad */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/30 via-zinc-900/10 to-transparent blur-[80px] -z-10 rounded-full pointer-events-none"></div>
          
          {/* The Hardware Bezel */}
          <div className="w-full border-[14px] md:border-[20px] border-[#0a0a0a] bg-[#0a0a0a] rounded-[2rem] md:rounded-[3rem] shadow-2xl relative flex items-center justify-center ring-1 ring-white/5">
            {/* The Camera Dot */}
            <div className="absolute top-1 md:top-2 left-1/2 -translate-x-1/2 w-2 h-2 md:w-3 md:h-3 rounded-full bg-zinc-800 z-20 shadow-[inset_0_1px_2px_rgba(0,0,0,0.8)]"></div>
            
            {/* Inner Screen Faint Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-zinc-500 to-transparent opacity-30 z-20 pointer-events-none"></div>
            
            {/* Screen Content (Video) */}
            <div className="w-full rounded-xl md:rounded-2xl overflow-hidden relative bg-zinc-900 flex">
              <video 
                autoPlay 
                loop 
                muted={true}
                playsInline={true}
                preload="auto"
                className="w-full h-auto z-10 relative block object-contain pointer-events-none"
                src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/9a805639780818f7e6c7788fb5c7a3ec.mp4"
              ></video>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Block 2: The Calendar Engine */}
      <section className="w-full py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          
          {/* Left Column (The Narrative) */}
          <motion.div 
            variants={textContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-2xl"
          >
            <motion.h2 variants={textItemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Sync your calendar.
            </motion.h2>
            <motion.p variants={textItemVariants} className="text-lg text-zinc-200 leading-relaxed">
              StudioSign hooks directly into your Google Calendar. Just enter the specific keywords you use for sessions—like "podcast" or "video"—and the app automatically updates your door displays whenever a matching booking hits your schedule.
            </motion.p>
          </motion.div>

          {/* Right Column (The Layered UI with Hover Swap) */}
          <div className="relative w-full aspect-square md:aspect-[4/3] flex items-center justify-center mt-12 lg:mt-0 group cursor-pointer">
            
            {/* Back Layer (Calendar Sync Image) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ ...defaultSpring, delay: 0.1 }}
              className="absolute top-0 right-0 w-[85%] md:w-[80%] z-10 group-hover:z-30 transition-colors"
            >
              {/* Inner div handles the tailwind smooth transforms so it doesn't conflict with Framer Motion's entry animation */}
              <div className="w-full transition-all duration-500 ease-out group-hover:scale-[1.05] group-hover:-translate-x-2 sm:group-hover:-translate-x-6 group-hover:translate-y-6 sm:group-hover:translate-y-12">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/ef3c64b3db82a39d1efd72c9b125040c.png" 
                  alt="Calendar Sync Configuration" 
                  draggable="false"
                  className="w-full h-auto rounded-2xl md:rounded-3xl shadow-2xl"
                />
              </div>
            </motion.div>
            
            {/* Front Layer (Word Filter Image) */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ ...defaultSpring, delay: 0.25 }}
              className="absolute bottom-4 left-0 w-[90%] md:w-[85%] z-20 group-hover:z-10 transition-colors"
            >
              <div className="w-full transition-all duration-500 ease-out group-hover:scale-90 group-hover:translate-x-8 sm:group-hover:translate-x-12 group-hover:-translate-y-8 sm:group-hover:-translate-y-16 group-hover:opacity-40">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/43f3652ae3ac3317100bb4ed7834c2d3.png" 
                  alt="Booking Names Word Filter" 
                  draggable="false"
                  className="w-full h-auto rounded-2xl md:rounded-3xl shadow-2xl drop-shadow-2xl"
                />
              </div>
            </motion.div>
            
          </div>
          
        </div>
      </section>

      {/* Block 3: The Main Display */}
      <section className="w-full py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          
          {/* Left Column (The iPad Video Mockup) */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={defaultSpring}
            className="relative w-full max-w-lg mx-auto bg-[#0a0a0a] p-[14px] rounded-[2rem] shadow-2xl ring-1 ring-white/5 order-last lg:order-first mt-12 lg:mt-0"
          >
            {/* Camera Dot */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-zinc-800 z-20 shadow-[inset_0_1px_2px_rgba(0,0,0,0.8)]"></div>
            
            {/* Screen Content (Video) */}
            <div className="w-full rounded-[1rem] overflow-hidden relative bg-zinc-900 flex">
              <video 
                autoPlay 
                loop 
                muted={true}
                playsInline={true}
                preload="auto"
                className="w-full h-auto z-10 relative block object-contain pointer-events-none"
                src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/8516bb5c9c8757ff73d2e6c1554c9adb.mp4"
              ></video>
            </div>
          </motion.div>

          {/* Right Column (The Narrative) */}
          <motion.div 
            variants={textContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-2xl"
          >
            <motion.h2 variants={textItemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Absolute boundaries. Zero confusion.
            </motion.h2>
            <motion.p variants={textItemVariants} className="text-lg text-zinc-200 leading-relaxed">
              Your door displays update automatically from open to standby to live recording. The screen shifts color in real time based on your calendar schedule, keeping your active production sets entirely insulated from walk-in disruptions.
            </motion.p>
          </motion.div>
          
        </div>
      </section>

      {/* Block 4: The Lobby Display */}
      <section className="w-full py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          
          {/* Left Column (The Narrative) */}
          <motion.div 
            variants={textContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-2xl"
          >
            <motion.h2 variants={textItemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Manage your lobby.
            </motion.h2>
            <motion.p variants={textItemVariants} className="text-lg text-zinc-200 leading-relaxed">
              Display your guest Wi-Fi credentials, add custom welcome text, and instantly generate a QR code for any link you paste in.
            </motion.p>
          </motion.div>

          {/* Right Column (The Landscape iPad Mockup) */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={defaultSpring}
            className="relative w-full max-w-3xl mx-auto bg-[#0a0a0a] p-[14px] rounded-[2rem] shadow-2xl ring-1 ring-white/5 flex items-center justify-center mt-12 lg:mt-0"
          >
            {/* Camera Dot */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-zinc-800 z-20 shadow-[inset_0_1px_2px_rgba(0,0,0,0.8)]"></div>
            
            {/* Screen Content (Image) */}
            <div className="w-full rounded-[1rem] overflow-hidden relative bg-zinc-900 flex">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/97d9c198b69add6e76bb09311705a54e.png" 
                alt="StudioSign Lobby Display Interface" 
                className="w-full h-auto z-10 relative block object-contain"
              />
            </div>
          </motion.div>
          
        </div>
      </section>

      {/* Block 5: The Notice Display */}
      <section className="w-full py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          
          {/* Left Column (The Notice Landscape iPad Mockup) */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={defaultSpring}
            className="relative w-full max-w-3xl mx-auto bg-[#0a0a0a] p-[14px] rounded-[2rem] shadow-2xl ring-1 ring-white/5 flex items-center justify-center order-last lg:order-first mt-12 lg:mt-0"
          >
            {/* Camera Dot */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-zinc-800 z-20 shadow-[inset_0_1px_2px_rgba(0,0,0,0.8)]"></div>
            
            {/* Screen Content (Image) */}
            <div className="w-full rounded-[1rem] overflow-hidden relative bg-zinc-900 flex">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/11662886f1c9ad072ccdab5f0d7fb4ca.png" 
                alt="StudioSign Notice Display Interface" 
                className="w-full h-auto z-10 relative block object-contain"
              />
            </div>
          </motion.div>

          {/* Right Column (The Narrative) */}
          <motion.div 
            variants={textContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-2xl"
          >
            <motion.h2 variants={textItemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Notes for any door.
            </motion.h2>
            <motion.p variants={textItemVariants} className="text-lg text-zinc-200 leading-relaxed">
              Type whatever you want from your desk. It updates the screen outside the room instantly.
            </motion.p>
          </motion.div>
          
        </div>
      </section>

      {/* Block 6: The Operator Control */}
      <section className="w-full py-32 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          
          {/* Left Column (The Narrative) */}
          <motion.div 
            variants={textContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="lg:col-span-5 xl:col-span-4 max-w-2xl px-4 lg:px-0"
          >
            <motion.h2 variants={textItemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Override your calendar.
            </motion.h2>
            <motion.p variants={textItemVariants} className="text-lg text-zinc-200 leading-relaxed">
              Change any room status instantly with a single click. Turning off the manual override snaps your displays right back to your scheduled calendar.
            </motion.p>
          </motion.div>

          {/* Right Column (The MacBook Pro Mockup with Interactive Demo) */}
          <div className="relative w-full lg:col-span-7 xl:col-span-8 mt-12 lg:mt-0 [perspective:1000px]">
            
            <motion.div 
              initial={{ opacity: 0, rotateX: 15, scale: 0.9, y: 40 }}
              whileInView={{ opacity: 1, rotateX: 0, scale: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={defaultSpring}
              className="relative w-full"
            >
              {/* Ambient Glow Behind Laptop */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-zinc-700/20 via-zinc-900/10 to-transparent blur-[60px] -z-10 rounded-full pointer-events-none"></div>

              <div className="relative w-full max-w-5xl mx-auto">
                {/* Screen Bezel */}
                <div className="relative w-full bg-[#0a0a0a] border-[8px] md:border-[12px] border-[#18181b] rounded-t-xl md:rounded-t-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden ring-1 ring-white/10 z-20">
                  
                  {/* Screen Glare Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none z-30"></div>

                  {/* MacBook Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 md:w-36 h-5 md:h-7 bg-[#18181b] rounded-b-xl md:rounded-b-2xl z-40 flex justify-center items-center">
                    {/* Subtle camera lens inside notch */}
                    <div className="w-1.5 h-1.5 rounded-full bg-black shadow-[inset_0_0_2px_rgba(255,255,255,0.2)]"></div>
                  </div>
                  
                  {/* Screen Content (Interactive Dashboard) */}
                  <div className="w-full aspect-[16/10] bg-[#0d0d0d] p-4 md:p-6 pt-8 md:pt-10 flex flex-col justify-between text-white select-none relative z-10">
                    
                    {/* Dynamic Status Monitor (Top Section) */}
                    <div className={`flex flex-col items-center justify-center flex-1 border rounded-xl p-4 transition-colors duration-300 ${statusConfig[overrideMode].containerClass} ${statusConfig[overrideMode].textClass}`}>
                      <div className="mb-2">
                        {overrideMode === 'closed' ? (
                          <span className="text-[9px] font-mono tracking-widest text-zinc-400 bg-zinc-900/50 px-2 py-0.5 rounded border border-zinc-800">
                            FOLLOWING SCHEDULE
                          </span>
                        ) : (
                          <span className="text-[9px] font-mono tracking-widest text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                            MANUAL OVERRIDE ACTIVE
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold tracking-tight mt-2">{statusConfig[overrideMode].heading}</h3>
                      <p className="text-xs text-zinc-300 mt-1">{statusConfig[overrideMode].sub}</p>
                    </div>

                    {/* 6-Button Grid Panel (Bottom Section) */}
                    <div className="grid grid-cols-2 gap-2 md:gap-3 mt-4">
                      {dashboardButtons.map((btn) => (
                        <button
                          key={btn.id}
                          onClick={() => setOverrideMode(btn.id)}
                          className={`flex items-center justify-center border rounded-lg text-[10px] md:text-xs font-semibold py-2.5 md:py-3 transition-colors duration-200 ${
                            overrideMode === btn.id
                              ? btn.activeClass
                              : 'bg-zinc-900/40 border-zinc-800/80 text-zinc-300 hover:text-white hover:border-zinc-700'
                          }`}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Base of the Laptop (3D Deck) */}
                <div className="relative w-[114%] h-5 md:h-8 bg-gradient-to-b from-[#3f3f46] via-[#27272a] to-[#09090b] -ml-[7%] rounded-b-xl md:rounded-b-2xl rounded-t-[2px] shadow-[0_30px_60px_-15px_rgba(0,0,0,1)] z-10 flex justify-center border-t border-white/20">
                  {/* Screen lid thumb groove */}
                  <div className="w-20 md:w-32 h-1.5 md:h-2.5 bg-[#141414] rounded-b-lg shadow-inner"></div>
                </div>
              </div>
            </motion.div>
          </div>
          
        </div>
      </section>

      {/* Premium CTA Block */}
      <section className="w-full pt-32 pb-32 relative overflow-hidden">
        <motion.div 
          variants={textContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="max-w-4xl mx-auto px-6 text-center relative z-10"
        >
          {/* Narrative & Button */}
          <motion.h2 variants={textItemVariants} className="text-5xl md:text-6xl font-bold text-white tracking-tight mb-6 text-center">
            Zero walk-ins. Complete control.
          </motion.h2>
          <motion.p variants={textItemVariants} className="text-xl text-zinc-200 max-w-xl mx-auto mb-12 leading-relaxed text-center">
            Put your studio displays on autopilot today.
          </motion.p>
          
          <motion.div variants={textItemVariants} className="flex justify-center items-center">
            <a 
              href="https://web.studiosign.app/signup"
              className="inline-flex items-center justify-center bg-white text-black px-8 py-3.5 text-lg font-medium rounded-full hover:bg-zinc-200 hover:scale-105 transition-all duration-300 w-full sm:w-auto"
            >
              Create Account
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Premium Structured Footer Layout */}
      <footer className="w-full border-t border-zinc-900/80 bg-[#09090b] pt-16 pb-16 mt-auto">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {/* Column 1: Brand */}
          <div className="flex flex-col gap-4">
            <span className="text-xl tracking-tight text-white select-none">
              <span className="font-light">Studio</span>
              <span className="font-bold">Sign</span>
            </span>
            <p className="text-sm text-zinc-500">
              &copy; 2026 Claxxiq LLC.
            </p>
          </div>

          {/* Column 2: Legal */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-semibold text-white tracking-widest uppercase">
              Legal
            </h4>
            <div className="flex flex-col gap-3 text-sm text-zinc-400">
              <a 
                href="https://gettermscdn.com/view/TeanA/privacy/en-us" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-white transition-colors w-fit"
              >
                Privacy Policy
              </a>
              <a 
                href="https://gettermscdn.com/view/TeanA/terms-of-service/en-us" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-white transition-colors w-fit"
              >
                Terms of Service
              </a>
              <a 
                href="https://gettermscdn.com/view/TeanA/acceptable-use/en-us" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-white transition-colors w-fit"
              >
                Acceptable Use Policy
              </a>
              <a 
                href="https://gettermscdn.com/view/TeanA/cookies/en-us" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:text-white transition-colors w-fit"
              >
                Cookie Policy
              </a>
            </div>
          </div>

          {/* Column 3: Compliance */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-semibold text-white tracking-widest uppercase">
              Compliance
            </h4>
            <div className="flex flex-col gap-3 text-sm text-zinc-400">
              <button 
                data-gt-cookie-widget-show="true" 
                className="hover:text-white transition-colors text-left w-fit"
              >
                Manage Cookie Preferences
              </button>
              <button 
                onClick={toggleAccessibility}
                className="hover:text-white transition-colors text-left w-fit"
              >
                Manage Accessibility
              </button>
            </div>
          </div>

          {/* Column 4: Help */}
          <div className="flex flex-col gap-4">
            <h4 className="text-xs font-semibold text-white tracking-widest uppercase">
              Help
            </h4>
            <div className="flex flex-col gap-3 text-sm text-zinc-400">
              <a 
                href="mailto:support@help.studiosign.app" 
                className="hover:text-white transition-colors w-fit"
              >
                Support
              </a>
            </div>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default LandingPage;